
import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CopyButton } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { AppSettings, CategoryKeywords } from '@/hooks/useSettings';
import { SimulationControls } from '@/components/admin/SimulationControls';
import { ActivityLogViewer } from '@/components/admin/ActivityLogViewer';
import { ModerationSettings } from '@/components/admin/ModerationSettings';
import { RecordingConsentViewer } from '@/components/admin/RecordingConsentViewer';
import { api, getClearstreamWebhookUrl } from '@/lib/api';
import { useBranding } from '@/lib/branding-context';
import { cn } from '@/lib/utils';

export type SettingsTab =
  | 'branding'
  | 'categories'
  | 'activity'
  | 'moderation'
  | 'responses'
  | 'stage'
  | 'integration'
  | 'practice';

/** Legacy query alias from older user-table consent links. */
export function normalizeSettingsTab(tab: string | null): SettingsTab | undefined {
  if (!tab) return undefined;
  if (tab === 'recording-consents') return 'responses';
  const known: SettingsTab[] = [
    'branding',
    'categories',
    'activity',
    'moderation',
    'responses',
    'stage',
    'integration',
    'practice',
  ];
  return known.includes(tab as SettingsTab) ? (tab as SettingsTab) : undefined;
}

export interface SettingsFormProps {
  settings: AppSettings;
  isSaving: boolean;
  onSave: (settings: Partial<AppSettings>) => Promise<boolean>;
  /** Reload settings from the server (e.g. after logo upload; avoids persisting presigned URLs). */
  refetchSettings?: () => Promise<void>;
  initialTab?: SettingsTab;
}

export function SettingsForm({
  settings,
  isSaving,
  onSave,
  refetchSettings,
  initialTab,
}: SettingsFormProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab ?? 'branding');

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  return (
    <Card variant="default" padding="none">
      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('branding')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'branding'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Branding
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'categories'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Category Keywords
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'activity'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Activity Log
          </button>
          <button
            onClick={() => setActiveTab('moderation')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'moderation'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Blocklist
          </button>
          <button
            onClick={() => setActiveTab('responses')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'responses'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Responses
          </button>
          <button
            onClick={() => setActiveTab('stage')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'stage'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Stage
          </button>
          <button
            onClick={() => setActiveTab('integration')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'integration'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Integrations
          </button>
          <button
            onClick={() => setActiveTab('practice')}
            className={cn(
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'practice'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            )}
          >
            Practice Simulation
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'branding' && (
          <BrandingSettingsEditor
            settings={settings}
            isSaving={isSaving}
            onSave={onSave}
            refetchSettings={refetchSettings}
          />
        )}
        {activeTab === 'categories' && (
          <CategoryKeywordsEditor
            keywords={settings.categoryKeywords}
            isSaving={isSaving}
            onSave={(categoryKeywords) => onSave({ categoryKeywords })}
          />
        )}
        {activeTab === 'activity' && <ActivityLogViewer />}
        {activeTab === 'moderation' && (
          <ModerationSettings
            settings={settings}
            isSaving={isSaving}
            onSave={onSave}
          />
        )}
        {activeTab === 'responses' && (
          <ResponsesSettingsEditor
            settings={settings}
            isSaving={isSaving}
            onSave={onSave}
          />
        )}
        {activeTab === 'stage' && (
          <StageSettingsEditor
            settings={settings}
            isSaving={isSaving}
            onSave={onSave}
          />
        )}
        {activeTab === 'integration' && (
          <IntegrationSettingsEditor
            settings={settings}
            isSaving={isSaving}
            onSave={onSave}
          />
        )}
        {activeTab === 'practice' && (
          <SimulationControls />
        )}
      </div>
    </Card>
  );
}

// Responses feature settings (enable toggle + recording consents)
interface ResponsesSettingsEditorProps {
  settings: AppSettings;
  isSaving: boolean;
  onSave: (settings: Partial<AppSettings>) => Promise<boolean>;
}

function ResponsesSettingsEditor({
  settings,
  isSaving,
  onSave,
}: ResponsesSettingsEditorProps) {
  const { refresh: refreshBranding } = useBranding();
  const [responsesEnabled, setResponsesEnabled] = useState(
    settings.responsesEnabled ?? true
  );

  useEffect(() => {
    setResponsesEnabled(settings.responsesEnabled ?? true);
  }, [settings.responsesEnabled]);

  const handleSave = async () => {
    const ok = await onSave({ responsesEnabled });
    if (ok) {
      await refreshBranding();
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">
            Prayer responses
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            When enabled, staff can request partner responses and partners can
            reply with text, audio, or video. Turning this off hides the feature
            from the sidebar and related controls. Existing consents and
            permissions are kept.
          </p>
        </div>
        <Checkbox
          label="Enable responses"
          description="Show Responses in the sidebar and allow requesting and sending partner responses"
          checked={responsesEnabled}
          onChange={(e) => setResponsesEnabled(e.target.checked)}
        />
        <div className="flex justify-end">
          <Button onClick={handleSave} isLoading={isSaving} type="button">
            Save
          </Button>
        </div>
      </div>

      {responsesEnabled && (
        <div className="border-t border-slate-200 pt-8 dark:border-slate-700">
          <RecordingConsentViewer />
        </div>
      )}
    </div>
  );
}

function StageSettingsEditor({
  settings,
  isSaving,
  onSave,
}: ResponsesSettingsEditorProps) {
  const { refresh: refreshBranding } = useBranding();
  const [stageEnabled, setStageEnabled] = useState(settings.stageEnabled ?? false);

  useEffect(() => {
    setStageEnabled(settings.stageEnabled ?? false);
  }, [settings.stageEnabled]);

  const handleSave = async () => {
    const ok = await onSave({ stageEnabled });
    if (ok) {
      await refreshBranding();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white">
          Stage tablet
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          When enabled, moderators can send prayers to the stage queue and read
          them from the Stage screen. Turning this off hides Stage from the
          sidebar and blocks stage actions.
        </p>
      </div>
      <Checkbox
        label="Enable tablet display"
        description="Show Stage in the sidebar and allow sending prayers to the stage tablet"
        checked={stageEnabled}
        onChange={(e) => setStageEnabled(e.target.checked)}
      />
      <div className="flex justify-end">
        <Button onClick={handleSave} isLoading={isSaving} type="button">
          Save
        </Button>
      </div>
    </div>
  );
}

// Branding Settings Editor
interface BrandingSettingsEditorProps {
  settings: AppSettings;
  isSaving: boolean;
  onSave: (settings: Partial<AppSettings>) => Promise<boolean>;
  refetchSettings?: () => Promise<void>;
}

function BrandingSettingsEditor({
  settings,
  isSaving,
  onSave,
  refetchSettings,
}: BrandingSettingsEditorProps) {
  const { refresh: refreshBranding } = useBranding();
  const [appName, setAppName] = useState(settings.appName ?? 'One Voice');
  const [feedbackFormUrl, setFeedbackFormUrl] = useState(settings.feedbackFormUrl ?? '');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);

  const logoSrc = settings.appLogoUrl ?? null;
  const iconSrc = settings.appIconUrl ?? null;

  useEffect(() => {
    setAppName(settings.appName ?? 'One Voice');
    setFeedbackFormUrl(settings.feedbackFormUrl ?? '');
  }, [settings.appName, settings.feedbackFormUrl]);

  const handleSave = async () => {
    const ok = await onSave({ appName, feedbackFormUrl });
    if (ok) {
      await refreshBranding();
    }
  };

  const handleUploadLogo = async (file: File) => {
    setLogoError(null);
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data, error } = await api.put<AppSettings>('/admin/branding/logo', form);
      if (error) setLogoError(error);
      if (data?.appLogoUrl) {
        // Reload admin settings from server (DB stores S3 key only; response has presigned URL).
        if (refetchSettings) await refetchSettings();
        await refreshBranding();
      }
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleUploadIcon = async (file: File) => {
    setIconError(null);
    setIconUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data, error } = await api.put<AppSettings>('/admin/branding/icon', form);
      if (error) setIconError(error);
      if (data?.appIconUrl) {
        if (refetchSettings) await refetchSettings();
        await refreshBranding();
      }
    } catch (e) {
      setIconError(e instanceof Error ? e.message : 'Failed to upload icon');
    } finally {
      setIconUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          Branding
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Customize the application name, logo, icon, and feedback link shown across the app.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Application name"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          placeholder="One Voice"
        />

        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-900 dark:text-white">Logo</h4>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={`${appName} logo`}
                className="h-10 max-w-[160px] object-contain bg-white"
              />
            ) : (
              <span className="text-sm text-slate-500 dark:text-slate-400">No logo uploaded</span>
            )}
          </div>
          <Input
            label="Upload new logo"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUploadLogo(f);
            }}
            helperText="Used on the expanded sidebar and public pages. Any PNG/JPEG is auto-resized to fit (max 800×160)."
          />
          {logoError && (
            <p className="text-sm text-red-600 dark:text-red-400">{logoError}</p>
          )}
        </div>

        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-900 dark:text-white">Icon</h4>
            {iconSrc ? (
              <img
                src={iconSrc}
                alt={`${appName} icon`}
                className="w-10 h-10 object-contain bg-white"
              />
            ) : (
              <span className="text-sm text-slate-500 dark:text-slate-400">No icon uploaded</span>
            )}
          </div>
          <Input
            label="Upload new icon"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUploadIcon(f);
            }}
            helperText="Used in the collapsed sidebar and browser tab. Any PNG/JPEG is auto-cropped to a square and saved at 512×512."
          />
          {iconError && (
            <p className="text-sm text-red-600 dark:text-red-400">{iconError}</p>
          )}
        </div>

        <Input
          label="Feedback form URL"
          value={feedbackFormUrl}
          onChange={(e) => setFeedbackFormUrl(e.target.value)}
          placeholder="https://..."
          helperText="Optional. Used only when GitHub feedback is not configured server-side (Asana embed fallback)."
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button onClick={handleSave} isLoading={isSaving} disabled={logoUploading || iconUploading}>
          Save Branding
        </Button>
      </div>
    </div>
  );
}

// Category Keywords Editor
interface CategoryKeywordsEditorProps {
  keywords: CategoryKeywords;
  isSaving: boolean;
  onSave: (keywords: CategoryKeywords) => Promise<boolean>;
}

function CategoryKeywordsEditor({
  keywords,
  isSaving,
  onSave,
}: CategoryKeywordsEditorProps) {
  const [localKeywords, setLocalKeywords] = useState(keywords);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    const categoryName = newCategory.toLowerCase().trim();
    if (localKeywords[categoryName]) return;
    
    setLocalKeywords({
      ...localKeywords,
      [categoryName]: [],
    });
    setNewCategory('');
  };

  const handleRemoveCategory = (category: string) => {
    const { [category]: removed, ...rest } = localKeywords;
    setLocalKeywords(rest);
  };

  const handleEditCategory = (category: string) => {
    setEditingCategory(category);
    const keywords = localKeywords[category];
    setEditValue(keywords ? keywords.join(', ') : '');
  };

  const handleSaveKeywords = (category: string) => {
    const newKeywordsList = editValue
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter((k) => k);
    
    setLocalKeywords({
      ...localKeywords,
      [category]: newKeywordsList,
    });
    setEditingCategory(null);
    setEditValue('');
  };

  const handleSave = async () => {
    await onSave(localKeywords);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          Category Keywords
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Configure keywords for automatic prayer request categorization.
        </p>
      </div>

      {/* Add new category */}
      <div className="flex gap-2">
        <Input
          placeholder="New category name"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
        />
        <Button onClick={handleAddCategory} variant="secondary">
          Add Category
        </Button>
      </div>

      {/* Category list */}
      <div className="space-y-4">
        {Object.entries(localKeywords).map(([category, categoryKeywords]) => (
          <div
            key={category}
            className="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-slate-900 dark:text-white capitalize">
                {category}
              </h4>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditCategory(category)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveCategory(category)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              </div>
            </div>
            
            {editingCategory === category ? (
              <div className="flex gap-2">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="health, healing, sick, surgery..."
                  helperText="Separate keywords with commas"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSaveKeywords(category)}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingCategory(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {categoryKeywords.length > 0 ? (
                  categoryKeywords.map((keyword) => (
                    <span
                      key={keyword}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                    >
                      {keyword}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-500 dark:text-slate-400 italic">
                    No keywords defined
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button onClick={handleSave} isLoading={isSaving}>
          Save Category Settings
        </Button>
      </div>
    </div>
  );
}

type IntegrationCardId = 'clearstream' | 'mailgun' | 'resend';

function IntegrationStatusBadge({ configured }: { configured: boolean }) {
  return (
    <span
      className={cn(
        'text-xs font-medium px-2 py-1 rounded',
        configured
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
      )}
    >
      {configured ? 'Configured' : 'Not configured'}
    </span>
  );
}

function ChevronDown({
  expanded,
  className,
}: {
  expanded: boolean;
  className?: string;
}) {
  return (
    <svg
      className={cn(
        'h-5 w-5 shrink-0 text-slate-500 transition-transform dark:text-slate-400',
        expanded && 'rotate-180',
        className
      )}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

interface IntegrationAccordionRowProps {
  title: string;
  configured: boolean;
  expanded: boolean;
  onToggle: () => void;
}

function IntegrationAccordionRow({
  title,
  configured,
  expanded,
  onToggle,
}: IntegrationAccordionRowProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
    >
      <span className="font-medium text-slate-900 dark:text-white">{title}</span>
      <div className="flex shrink-0 items-center gap-2">
        <IntegrationStatusBadge configured={configured} />
        <ChevronDown expanded={expanded} />
      </div>
    </button>
  );
}

function isMaskedSecret(value: string) {
  return Boolean(value && value.startsWith('***'));
}

// Integration Settings Editor
interface IntegrationSettingsEditorProps {
  settings: AppSettings;
  isSaving: boolean;
  onSave: (settings: Partial<AppSettings>) => Promise<boolean>;
}

function IntegrationSettingsEditor({
  settings,
  isSaving,
  onSave,
}: IntegrationSettingsEditorProps) {
  const [openCard, setOpenCard] = useState<IntegrationCardId | null>(null);

  const toggleCard = (id: IntegrationCardId) => {
    setOpenCard((prev) => (prev === id ? null : id));
  };

  // Clearstream
  const [clearstreamEndpoint, setClearstreamEndpoint] = useState(
    settings.clearstreamEndpoint || ''
  );
  const [clearstreamApiKey, setClearstreamApiKey] = useState(settings.clearstreamApiKey || '');
  const [clearstreamWebhookSecret, setClearstreamWebhookSecret] = useState(
    settings.clearstreamWebhookSecret || ''
  );
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test message from One Voice');
  const [testSending, setTestSending] = useState(false);
  const [testSmsResult, setTestSmsResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Shared sender (persisted once; Mailgun / Resend both use it)
  const [integrationFromEmail, setIntegrationFromEmail] = useState(settings.fromEmail ?? '');

  // Mailgun
  const [mailgunApiKey, setMailgunApiKey] = useState('');
  const [mailgunDomain, setMailgunDomain] = useState(settings.emailDomain ?? '');
  const [mailgunEmailTestAddr, setMailgunEmailTestAddr] = useState('');
  const [mailgunEmailSending, setMailgunEmailSending] = useState(false);
  const [mailgunEmailResult, setMailgunEmailResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Resend
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendEmailTestAddr, setResendEmailTestAddr] = useState('');
  const [resendEmailSending, setResendEmailSending] = useState(false);
  const [resendEmailResult, setResendEmailResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    setClearstreamEndpoint(settings.clearstreamEndpoint ?? '');
    setClearstreamApiKey(settings.clearstreamApiKey ?? '');
    setClearstreamWebhookSecret(settings.clearstreamWebhookSecret ?? '');
  }, [settings.clearstreamEndpoint, settings.clearstreamApiKey, settings.clearstreamWebhookSecret]);

  useEffect(() => {
    setIntegrationFromEmail(settings.fromEmail ?? '');
  }, [settings.fromEmail]);

  const provider = (settings.emailProvider ?? '').toLowerCase().trim();

  useEffect(() => {
    if (provider === 'mailgun') {
      setMailgunApiKey(settings.emailApiKey ?? '');
      setMailgunDomain(settings.emailDomain ?? '');
    } else {
      setMailgunApiKey('');
      setMailgunDomain('');
    }
  }, [provider, settings.emailApiKey, settings.emailDomain]);

  useEffect(() => {
    if (provider === 'resend') {
      setResendApiKey(settings.emailApiKey ?? '');
    } else {
      setResendApiKey('');
    }
  }, [provider, settings.emailApiKey]);

  const handleSaveClearstream = async () => {
    setTestSmsResult(null);
    const payload: Partial<AppSettings> = {
      clearstreamEndpoint: clearstreamEndpoint.trim(),
    };
    if (clearstreamApiKey && !isMaskedSecret(clearstreamApiKey)) {
      payload.clearstreamApiKey = clearstreamApiKey;
    }
    if (clearstreamWebhookSecret && !isMaskedSecret(clearstreamWebhookSecret)) {
      payload.clearstreamWebhookSecret = clearstreamWebhookSecret;
    }
    await onSave(payload);
  };

  const handleSaveMailgun = async () => {
    setMailgunEmailResult(null);
    const payload: Partial<AppSettings> = {
      emailProvider: 'mailgun',
      fromEmail: integrationFromEmail.trim(),
      emailDomain: mailgunDomain.trim(),
    };
    if (mailgunApiKey && !isMaskedSecret(mailgunApiKey)) {
      payload.emailApiKey = mailgunApiKey;
    }
    await onSave(payload);
  };

  const handleSaveResend = async () => {
    setResendEmailResult(null);
    const payload: Partial<AppSettings> = {
      emailProvider: 'resend',
      fromEmail: integrationFromEmail.trim(),
      emailDomain: '',
    };
    if (resendApiKey && !isMaskedSecret(resendApiKey)) {
      payload.emailApiKey = resendApiKey;
    }
    await onSave(payload);
  };

  const handleSendTestSms = async () => {
    const to = testPhone.trim();
    if (!to) {
      setTestSmsResult({ success: false, message: 'Enter a phone number (e.g. +15551234567)' });
      return;
    }
    setTestSending(true);
    setTestSmsResult(null);
    try {
      const { data, error } = await api.post<{ sent: boolean }>('/admin/test-sms', {
        to,
        message: testMessage.trim() || undefined,
      });
      if (error) {
        setTestSmsResult({ success: false, message: error });
        return;
      }
      if (data?.sent) {
        setTestSmsResult({ success: true, message: 'Test SMS sent successfully.' });
      } else {
        setTestSmsResult({ success: false, message: 'Send failed. Check Clearstream settings.' });
      }
    } catch (err) {
      setTestSmsResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send test SMS',
      });
    } finally {
      setTestSending(false);
    }
  };

  const handleSendTestMailgun = async () => {
    const to = mailgunEmailTestAddr.trim();
    if (!to) {
      setMailgunEmailResult({ success: false, message: 'Enter a recipient email address.' });
      return;
    }
    setMailgunEmailSending(true);
    setMailgunEmailResult(null);
    try {
      const { data, error } = await api.post<{ sent: boolean }>('/admin/test-email', { to });
      if (error) {
        setMailgunEmailResult({ success: false, message: error });
        return;
      }
      if (data?.sent) {
        setMailgunEmailResult({ success: true, message: 'Test email sent successfully.' });
      } else {
        setMailgunEmailResult({ success: false, message: 'Send failed.' });
      }
    } catch (err) {
      setMailgunEmailResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send test email',
      });
    } finally {
      setMailgunEmailSending(false);
    }
  };

  const handleSendTestResend = async () => {
    const to = resendEmailTestAddr.trim();
    if (!to) {
      setResendEmailResult({ success: false, message: 'Enter a recipient email address.' });
      return;
    }
    setResendEmailSending(true);
    setResendEmailResult(null);
    try {
      const { data, error } = await api.post<{ sent: boolean }>('/admin/test-email', { to });
      if (error) {
        setResendEmailResult({ success: false, message: error });
        return;
      }
      if (data?.sent) {
        setResendEmailResult({ success: true, message: 'Test email sent successfully.' });
      } else {
        setResendEmailResult({ success: false, message: 'Send failed.' });
      }
    } catch (err) {
      setResendEmailResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send test email',
      });
    } finally {
      setResendEmailSending(false);
    }
  };

  const clearstreamConfigured = settings.clearstreamConfigured === true;
  const clearstreamWebhookUrl = useMemo(() => getClearstreamWebhookUrl(), []);
  const mailgunConfigured =
    provider === 'mailgun' &&
    Boolean(settings.emailApiKey && String(settings.emailApiKey).length > 0) &&
    Boolean((settings.emailDomain ?? '').trim()) &&
    Boolean((settings.fromEmail ?? '').trim());
  const resendConfigured =
    provider === 'resend' &&
    Boolean(settings.emailApiKey && String(settings.emailApiKey).length > 0) &&
    Boolean((settings.fromEmail ?? '').trim());

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          Integration Settings
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Configure external service integrations. Expand a row to edit API credentials. Only one transactional email
          provider can be active at a time; saving Mailgun or Resend sets that provider as active.
        </p>
      </div>

      <div className="space-y-3">
        {/* Clearstream */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
          <IntegrationAccordionRow
            title="Clearstream (SMS)"
            configured={clearstreamConfigured}
            expanded={openCard === 'clearstream'}
            onToggle={() => toggleCard('clearstream')}
          />
          {openCard === 'clearstream' && (
            <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4 space-y-4">
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-slate-900 dark:text-white mb-1">
                    Inbound webhook
                  </h5>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Paste the webhook URL into Clearstream and use the same webhook authentication key there and here.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="clearstream-webhook-url"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
                  >
                    Webhook URL
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <input
                      id="clearstream-webhook-url"
                      type="text"
                      readOnly
                      value={clearstreamWebhookUrl}
                      className={cn(
                        'block w-full min-h-[44px] rounded-lg border bg-slate-50 dark:bg-slate-900',
                        'border-slate-300 dark:border-slate-600 px-4 py-3 text-base',
                        'text-slate-900 dark:text-white font-mono text-sm'
                      )}
                    />
                    <CopyButton text={clearstreamWebhookUrl} size="sm" className="shrink-0" />
                  </div>
                  <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                    Paste this URL into Clearstream as the inbound webhook (POST).
                  </p>
                </div>
                <Input
                  label="Webhook authentication key"
                  type="password"
                  value={clearstreamWebhookSecret}
                  onChange={(e) => setClearstreamWebhookSecret(e.target.value)}
                  placeholder="Enter your webhook key"
                  helperText="Inbound webhook verification only (stored securely). Separate from the API key."
                />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Expected text format:{' '}
                  <span className="font-mono text-slate-700 dark:text-slate-300">
                    TSCPRAY Joe Please pray for the homeless in my Brooklyn neighborhood.
                  </span>
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
                <div>
                  <h5 className="font-medium text-slate-900 dark:text-white mb-1">
                    Outbound SMS
                  </h5>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Uses the API key (a separate credential from the webhook authentication key).
                  </p>
                </div>
                <Input
                  label="API Endpoint"
                  value={clearstreamEndpoint}
                  onChange={(e) => setClearstreamEndpoint(e.target.value)}
                  placeholder="https://api.getclearstream.com/v1"
                  helperText="The Clearstream API base URL (defaults to official API if empty)"
                />
                <Input
                  label="API Key"
                  type="password"
                  value={clearstreamApiKey}
                  onChange={(e) => setClearstreamApiKey(e.target.value)}
                  placeholder="Enter your API key"
                  helperText="Outbound SMS only (stored securely). Separate from the webhook authentication key."
                />
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h5 className="font-medium text-slate-900 dark:text-white mb-2">
                  Test outbound SMS
                </h5>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  Send a test message to verify your API key and outbound SMS.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    label="Phone number"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+15551234567"
                    className="sm:max-w-[200px]"
                  />
                  <Input
                    label="Message (optional)"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Test message from One Voice"
                    className="flex-1"
                  />
                  <div className="flex items-end">
                    <Button
                      onClick={handleSendTestSms}
                      isLoading={testSending}
                      disabled={!clearstreamConfigured || testSending}
                      type="button"
                    >
                      Send test SMS
                    </Button>
                  </div>
                </div>
                {testSmsResult && (
                  <p
                    className={cn(
                      'mt-2 text-sm',
                      testSmsResult.success
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {testSmsResult.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveClearstream} isLoading={isSaving} type="button">
                  Save Clearstream
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mailgun */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
          <IntegrationAccordionRow
            title="Mailgun (email)"
            configured={mailgunConfigured}
            expanded={openCard === 'mailgun'}
            onToggle={() => toggleCard('mailgun')}
          />
          {openCard === 'mailgun' && (
            <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4 space-y-4">
              <Input
                label="API Key"
                type="password"
                value={mailgunApiKey}
                onChange={(e) => setMailgunApiKey(e.target.value)}
                placeholder="Your Mailgun private API key"
                helperText="Stored securely. Leave unchanged to keep the current key when you see a masked value."
              />
              <Input
                label="Sending domain"
                value={mailgunDomain}
                onChange={(e) => setMailgunDomain(e.target.value)}
                placeholder="mg.yourdomain.com"
                helperText="Mailgun domain used to send (e.g. sandbox or verified domain)"
              />
              <Input
                label="From email"
                value={integrationFromEmail}
                onChange={(e) => setIntegrationFromEmail(e.target.value)}
                placeholder="noreply@yourdomain.com"
                helperText="Must be allowed for your Mailgun domain. Recipients see the app name as the sender."
              />

              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h5 className="font-medium text-slate-900 dark:text-white mb-2">Test email</h5>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  Save Mailgun settings first, then send a test using the active configuration.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    label="Recipient"
                    type="email"
                    value={mailgunEmailTestAddr}
                    onChange={(e) => setMailgunEmailTestAddr(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1"
                  />
                  <div className="flex items-end">
                    <Button
                      onClick={handleSendTestMailgun}
                      isLoading={mailgunEmailSending}
                      disabled={!mailgunConfigured || mailgunEmailSending}
                      type="button"
                    >
                      Send test email
                    </Button>
                  </div>
                </div>
                {mailgunEmailResult && (
                  <p
                    className={cn(
                      'mt-2 text-sm',
                      mailgunEmailResult.success
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {mailgunEmailResult.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveMailgun} isLoading={isSaving} type="button">
                  Save Mailgun
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Resend */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
          <IntegrationAccordionRow
            title="Resend (email)"
            configured={resendConfigured}
            expanded={openCard === 'resend'}
            onToggle={() => toggleCard('resend')}
          />
          {openCard === 'resend' && (
            <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4 space-y-4">
              <Input
                label="API Key"
                type="password"
                value={resendApiKey}
                onChange={(e) => setResendApiKey(e.target.value)}
                placeholder="re_..."
                helperText="Stored securely. Leave unchanged to keep the current key when you see a masked value."
              />
              <Input
                label="From email"
                value={integrationFromEmail}
                onChange={(e) => setIntegrationFromEmail(e.target.value)}
                placeholder="onboarding@resend.dev or your verified sender"
                helperText="Must match a sender Resend allows for your account. Recipients see the app name as the sender."
              />

              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h5 className="font-medium text-slate-900 dark:text-white mb-2">Test email</h5>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  Save Resend settings first, then send a test using the active configuration.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    label="Recipient"
                    type="email"
                    value={resendEmailTestAddr}
                    onChange={(e) => setResendEmailTestAddr(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1"
                  />
                  <div className="flex items-end">
                    <Button
                      onClick={handleSendTestResend}
                      isLoading={resendEmailSending}
                      disabled={!resendConfigured || resendEmailSending}
                      type="button"
                    >
                      Send test email
                    </Button>
                  </div>
                </div>
                {resendEmailResult && (
                  <p
                    className={cn(
                      'mt-2 text-sm',
                      resendEmailResult.success
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {resendEmailResult.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveResend} isLoading={isSaving} type="button">
                  Save Resend
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
